import { useCallback, useEffect, useRef } from 'react';

import { ownerDocument } from '../../../../../utils/dom';
import useEventCallback from '../../../../../utils/hooks/use-event-callback';
import useIsomorphicLayoutEffect from '../../../../../utils/hooks/use-isomorphic-layout-effect';
import {
  areValuesEqual,
  clamp,
  constrainRangeValue,
  doesSupportTouchActionNone,
  findClosest,
  focusThumb,
  INTENTIONAL_DRAG_COUNT_THRESHOLD,
  percentToValue,
  roundValueToStep,
  trackFinger,
} from '../utils';

import type * as React from 'react';

import type { EventHandlers } from '../../../../../utils/dom';
import type { Axis, Mark, SliderValue } from '../types';

interface FingerNewValue {
  activeIndex: number;
  newValue: SliderValue;
}

interface UseSliderPointerInteractionParameters {
  axis: Axis;
  disabled: boolean;
  disableSwap: boolean;
  handleChange?: (event: Event | React.SyntheticEvent, value: SliderValue, activeThumb: number) => void;
  marks: Mark[];
  marksValues: number[];
  max: number;
  min: number;
  minDistance: number;
  onChangeCommitted?: (event: Event | React.SyntheticEvent, value: SliderValue) => void;
  range: boolean;
  readOnly: boolean;
  setActive: (index: number) => void;
  setDragging: (value: boolean) => void;
  setOpen: (index: number) => void;
  setValueState: (value: SliderValue) => void;
  sliderRef: React.RefObject<HTMLSpanElement | null>;
  step: null | number;
  valueDerived: SliderValue;
  values: number[];
}

interface UseSliderPointerInteractionResult {
  createHandleMouseDown: (otherHandlers: EventHandlers) => (event: React.MouseEvent<HTMLSpanElement>) => void;
}

/**
 * Fires the change callback only when the new value differs from the previous one.
 * Prevents redundant updates during pointer moves that don't cross a step boundary.
 */
const emitIfChanged = (
  emitChange: UseSliderPointerInteractionParameters['handleChange'],
  event: Event | React.SyntheticEvent,
  newValue: SliderValue,
  activeThumb: number,
  previousValue: SliderValue,
): void => {
  if (emitChange && !areValuesEqual(newValue, previousValue)) {
    emitChange(event, newValue, activeThumb);
  }
};

/**
 * Manages mouse and touch pointer interactions for the slider.
 *
 * Handles the full drag lifecycle:
 * 1. **Pointer down** (mousedown/touchstart) — determines the clicked position,
 *    finds the closest thumb, jumps it to the pointer, and starts listening for moves.
 * 2. **Pointer move** (mousemove/touchmove) — tracks the finger position, updates
 *    the thumb value in real-time, and triggers the dragging visual state after
 *    `INTENTIONAL_DRAG_COUNT_THRESHOLD` moves.
 * 3. **Pointer up** (mouseup/touchend) — commits the final value and cleans up listeners.
 *
 * Touch events are attached directly to the slider DOM element (not via React)
 * to control `passive` behavior for scroll prevention.
 *
 * @returns `createHandleMouseDown` — a factory that produces the `onMouseDown` handler
 *          for the slider root, composable with external event handlers.
 */
const useSliderPointerInteraction = ({
  axis,
  disabled,
  disableSwap,
  handleChange,
  marks,
  marksValues,
  max,
  min,
  minDistance,
  onChangeCommitted,
  range,
  readOnly,
  setActive,
  setDragging,
  setOpen,
  setValueState,
  sliderRef,
  step,
  valueDerived,
  values,
}: UseSliderPointerInteractionParameters): UseSliderPointerInteractionResult => {
  /** Identifier of the touch we're tracking (for multi-touch support). */
  const touchId = useRef<number | undefined>(undefined);
  /** Number of move events since the interaction started. Used to detect intentional drags. */
  const moveCount = useRef(0);
  /** Remembers which thumb was last active during a drag to avoid thumb-switching mid-drag. */
  const previousIndex = useRef<number | undefined>(undefined);
  /** Ref to the latest touchEnd handler so event listeners always call the current version. */
  const handleTouchEndRef = useRef<(nativeEvent: MouseEvent | TouchEvent) => void>(() => {});

  const getTargetMarkValue = useCallback(
    (target: EventTarget | null): null | number => {
      if (!(target instanceof Element)) {
        return null;
      }

      const markElement = target.closest<HTMLElement>('[data-mark-index]');
      const markIndex = Number(markElement?.dataset.markIndex);

      if (!Number.isInteger(markIndex)) {
        return null;
      }

      return marks[markIndex]?.value ?? null;
    },
    [marks],
  );

  const getResolvedValue = useCallback(
    (rawValue: number, move = false): FingerNewValue => {
      let newValue = rawValue;

      if (step) {
        newValue = roundValueToStep(newValue, step, min);
      } else {
        const closestIndex = findClosest(marksValues, newValue);
        newValue = marksValues[closestIndex] ?? min;
      }

      newValue = clamp(newValue, min, max);
      let activeIndex = 0;

      if (range) {
        activeIndex = move ? (previousIndex.current ?? 0) : findClosest(values, newValue);
        const constrainedRange = constrainRangeValue(values, newValue, activeIndex, {
          disableSwap,
          max,
          min,
          minDistance,
        });

        previousIndex.current = constrainedRange.activeIndex;

        return { activeIndex: constrainedRange.activeIndex, newValue: constrainedRange.value as [number, number] };
      }

      return { activeIndex, newValue };
    },
    [disableSwap, marksValues, max, min, minDistance, range, step, values],
  );

  const selectMarkValue = useCallback(
    (event: Event | React.SyntheticEvent, markValue: number) => {
      const { activeIndex, newValue } = getResolvedValue(markValue);

      previousIndex.current = activeIndex;
      focusThumb({ activeIndex, sliderRef });
      setValueState(newValue);
      emitIfChanged(handleChange, event, newValue, activeIndex, valueDerived ?? min);
      onChangeCommitted?.(event, newValue);
    },
    [getResolvedValue, handleChange, min, onChangeCommitted, setValueState, sliderRef, valueDerived],
  );

  /**
   * Converts a pointer position (finger coordinates) to a slider value and determines
   * which thumb should be active.
   *
   * @param finger - The pointer's {x, y} client coordinates.
   * @param move - Whether this is a move event (vs. initial click/tap). When true,
   *               the active thumb is kept stable to prevent switching mid-drag.
   */
  const getFingerNewValue = useCallback(
    ({ finger, move = false }: { finger: { x: number; y: number }; move?: boolean }): FingerNewValue => {
      const slider = sliderRef.current;

      if (!slider) {
        return { activeIndex: 0, newValue: range ? (values as [number, number]) : (values[0] ?? min) };
      }

      // Convert the finger position to a 0–1 percentage along the track
      const { bottom, height, left, width } = slider.getBoundingClientRect();
      let percent = 0;

      if (axis.startsWith('vertical')) {
        // Vertical: 0% at the bottom, 100% at the top
        percent = height > 0 ? (bottom - finger.y) / height : 0;
      } else {
        // Horizontal: 0% at the left, 100% at the right
        percent = width > 0 ? (finger.x - left) / width : 0;
      }

      if (axis.includes('-reverse')) {
        percent = 1 - percent;
      }

      // Convert percentage to an absolute value and snap to the step grid
      return getResolvedValue(percentToValue(percent, min, max), move);
    },
    [axis, getResolvedValue, max, min, range, sliderRef, values],
  );

  /** Removes all document-level pointer listeners that were attached during a drag. */
  const stopListening = useCallback(
    (
      touchMoveHandler: (nativeEvent: MouseEvent | TouchEvent) => void,
      touchEndHandler: (nativeEvent: MouseEvent | TouchEvent) => void,
    ) => {
      const doc = ownerDocument(sliderRef.current);

      doc.removeEventListener('mousemove', touchMoveHandler);
      doc.removeEventListener('mouseup', touchEndHandler);
      doc.removeEventListener('touchmove', touchMoveHandler);
      doc.removeEventListener('touchend', touchEndHandler);
      doc.removeEventListener('touchcancel', touchEndHandler);
    },
    [sliderRef],
  );

  /** Handles continuous pointer movement during a drag. */
  const handleTouchMove = useEventCallback((nativeEvent: MouseEvent | TouchEvent) => {
    const finger = trackFinger(nativeEvent, touchId);

    if (!finger) {
      return;
    }

    moveCount.current += 1;

    // Safety: if the mouse button was released outside the window (e.g. alt-tabbed),
    // `buttons === 0` means no button is pressed — treat as a drag end.
    if (nativeEvent.type === 'mousemove' && 'buttons' in nativeEvent && nativeEvent.buttons === 0) {
      handleTouchEndRef.current(nativeEvent);
      return;
    }

    const { activeIndex, newValue } = getFingerNewValue({ finger, move: true });

    focusThumb({ activeIndex, setActive, sliderRef });
    setValueState(newValue);

    if (moveCount.current > INTENTIONAL_DRAG_COUNT_THRESHOLD) {
      setDragging(true);
    }

    emitIfChanged(handleChange, nativeEvent, newValue, activeIndex, valueDerived ?? min);
  });

  /** Finalizes a drag: commits the value, resets interaction state, and removes listeners. */
  const handleTouchEnd = useEventCallback((nativeEvent: MouseEvent | TouchEvent) => {
    const finger = trackFinger(nativeEvent, touchId);

    setDragging(false);
    touchId.current = undefined;

    if (!finger) {
      stopListening(handleTouchMove, handleTouchEndRef.current);
      return;
    }

    const { newValue } = getFingerNewValue({ finger, move: true });

    setActive(-1);

    if (nativeEvent.type === 'touchend' || nativeEvent.type === 'touchcancel') {
      setOpen(-1);
    }

    onChangeCommitted?.(nativeEvent, newValue);
    stopListening(handleTouchMove, handleTouchEndRef.current);
  });

  useIsomorphicLayoutEffect(() => {
    handleTouchEndRef.current = handleTouchEnd;
  }, [handleTouchEnd]);

  /** Initiates a touch drag: records the touch identifier, jumps the thumb, and attaches move/end listeners. */
  const handleTouchStart = useEventCallback((nativeEvent: TouchEvent) => {
    if (disabled || readOnly) {
      return;
    }

    if (!doesSupportTouchActionNone()) {
      nativeEvent.preventDefault();
    }

    const touch = nativeEvent.changedTouches[0];

    if (touch) {
      touchId.current = touch.identifier;
    }

    const targetMarkValue = getTargetMarkValue(nativeEvent.target);

    if (targetMarkValue != null) {
      selectMarkValue(nativeEvent, targetMarkValue);
      return;
    }

    const finger = trackFinger(nativeEvent, touchId);

    if (finger) {
      const { activeIndex, newValue } = getFingerNewValue({ finger });

      focusThumb({ activeIndex, setActive, sliderRef });
      setValueState(newValue);
      emitIfChanged(handleChange, nativeEvent, newValue, activeIndex, valueDerived ?? min);
    }

    moveCount.current = 0;

    const doc = ownerDocument(sliderRef.current);
    doc.addEventListener('touchmove', handleTouchMove, { passive: true });
    doc.addEventListener('touchend', handleTouchEnd, { passive: true });
    doc.addEventListener('touchcancel', handleTouchEnd, { passive: true });
  });

  // Attach the touchstart listener directly to the DOM element (not via React props)
  // so we can control `passive` based on touch-action support. When `touch-action: none`
  // is supported, we use passive listeners; otherwise, the handler calls preventDefault().
  useEffect(() => {
    const slider = sliderRef.current;

    if (!slider) {
      return;
    }

    slider.addEventListener('touchstart', handleTouchStart, {
      passive: doesSupportTouchActionNone(),
    });

    return () => {
      slider.removeEventListener('touchstart', handleTouchStart);
      stopListening(handleTouchMove, handleTouchEndRef.current);
    };
  }, [handleTouchMove, handleTouchStart, sliderRef, stopListening]);

  // Clean up any active drag listeners when the slider becomes disabled
  useEffect(() => {
    if (disabled) {
      stopListening(handleTouchMove, handleTouchEndRef.current);
    }
  }, [disabled, handleTouchMove, stopListening]);

  const createHandleMouseDown = (otherHandlers: EventHandlers) => (event: React.MouseEvent<HTMLSpanElement>) => {
    otherHandlers.onMouseDown?.(event);

    if (disabled || readOnly || event.defaultPrevented || event.button !== 0) {
      return;
    }

    event.preventDefault();

    const finger = trackFinger(event, touchId);

    const targetMarkValue = getTargetMarkValue(event.target);

    if (targetMarkValue != null) {
      selectMarkValue(event, targetMarkValue);
      return;
    }

    if (finger) {
      const { activeIndex, newValue } = getFingerNewValue({ finger });

      focusThumb({ activeIndex, setActive, sliderRef });
      setValueState(newValue);
      emitIfChanged(handleChange, event, newValue, activeIndex, valueDerived ?? min);
    }

    moveCount.current = 0;

    const doc = ownerDocument(sliderRef.current);
    doc.addEventListener('mousemove', handleTouchMove, { passive: true });
    doc.addEventListener('mouseup', handleTouchEnd);
  };

  return { createHandleMouseDown };
};

export default useSliderPointerInteraction;
